/**
 * Portfolio Thesis API client methods.
 *
 * Manages portfolio-level investment thesis with multi-version support.
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';

export interface SoulParams {
  target_positions: string | null;
  max_single_weight: string | null;
  rebalance_direction: string | null;
  exit_trigger: string | null;
  turnover_tolerance: string | null;
}

export interface PortfolioThesis {
  id: number;
  user_id: number;
  target_risk_level: 'Conservative' | 'Moderate' | 'Balanced' | 'Growth' | null;
  target_return_level: number | null;
  target_cash_flow: number | null;
  comment: string | null;
  style_name: string | null;
  style_description: string | null;
  target_positions: string | null;
  max_single_weight: string | null;
  rebalance_direction: string | null;
  exit_trigger: string | null;
  turnover_tolerance: string | null;
  es_target_override: number | null;
  max_single_weight_pct: number | null;
  /** Server-derived on write: the resolved ε (profile or override). */
  es_target: number | null;
  updated_at: string;
}

export interface PortfolioThesisCreateRequest {
  target_risk_level?: 'Conservative' | 'Moderate' | 'Balanced' | 'Growth' | null;
  target_return_level?: number | null;
  target_cash_flow?: number | null;
  comment?: string | null;
  style_name?: string | null;
  style_description?: string | null;
  target_positions?: string | null;
  max_single_weight?: string | null;
  rebalance_direction?: string | null;
  exit_trigger?: string | null;
  turnover_tolerance?: string | null;
  es_target_override?: number | null;
  max_single_weight_pct?: number | null;
}

export interface PortfolioThesisUpdateRequest {
  target_risk_level?: 'Conservative' | 'Moderate' | 'Balanced' | 'Growth' | null;
  target_return_level?: number | null;
  target_cash_flow?: number | null;
  comment?: string | null;
  style_name?: string | null;
  style_description?: string | null;
  target_positions?: string | null;
  max_single_weight?: string | null;
  rebalance_direction?: string | null;
  exit_trigger?: string | null;
  turnover_tolerance?: string | null;
  /** Send null to clear the declared ES override. */
  es_target_override?: number | null;
  max_single_weight_pct?: number | null;
}

const isClearableNumericField = (key: string) =>
  key === 'target_return_level' ||
  key === 'target_cash_flow' ||
  key === 'es_target_override' ||
  key === 'max_single_weight_pct';

/**
 * Get all thesis versions for current user.
 * Returns array of theses sorted by updated_at DESC (latest first).
 * Empty array if no thesis exists.
 */
export async function getPortfolioThesis(): Promise<PortfolioThesis[]> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/portfolio-thesis`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to get portfolio thesis');
  }

  return response.json();
}

/**
 * Create a new portfolio thesis (always creates new version).
 */
export async function createPortfolioThesis(
  data: PortfolioThesisCreateRequest
): Promise<PortfolioThesis> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  // Filter out null/undefined values.
  const requestData = Object.fromEntries(
    Object.entries(data).filter(([_, v]) => v !== null && v !== undefined)
  );

  const response = await fetch(`${API_CONFIG.baseURL}/portfolio-thesis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to create portfolio thesis');
  }

  return response.json();
}

/**
 * Update portfolio thesis (creates new version).
 */
export async function updatePortfolioThesis(
  thesisId: number,
  data: PortfolioThesisUpdateRequest
): Promise<PortfolioThesis> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  // Preserve explicit nulls for clearable numeric fields; omit only unset values.
  const requestData: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if ((value !== null && value !== undefined) || isClearableNumericField(key)) {
      requestData[key] = value;
    }
  }
  const response = await fetch(`${API_CONFIG.baseURL}/portfolio-thesis/${thesisId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to update portfolio thesis');
  }

  return response.json();
}

/**
 * Delete a specific portfolio thesis version.
 */
export async function deletePortfolioThesis(thesisId: number): Promise<void> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/portfolio-thesis/${thesisId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to delete portfolio thesis');
  }
}
